import isLocal from '../common/isLocal.js'
import miniPackages from '../viewer/miniPackages.js'

export default function (window, requirejs, prefetch, defineExperiment) {
  if (prefetch && window.define) {
    const dummy = () => ({})
    const define = window.define
    window.define = function (...args) {
      for (let i = 0; i < args.length; ++i) {
        if (typeof args[i] === 'function') {
          args[i] = dummy
        }
      }
      return define.apply(this, args)
    }
    window.define.amd = define.amd
    const rjs = requirejs
    window.requirejs = requirejs = function (dependencies) {
      return rjs.call(this, dependencies, dummy)
    }
  }


  function notifyParent(msg) {
    try {
      window.parent.postMessage(msg, '*')
    } catch (e) {
      //ignore
    }
  }

  notifyParent('santaStart')

  function render(isServerSide, isPreview, isExternalPreview, contentCache, queryUtil, siteModel, wixBiSession, mobileView, dynamicModelUrl, config) {
    function addConditionalDependencies(pkgs) {
      function shouldLoadPackageFor(applicationType) {
        const map = siteModel.rendererModel.clientSpecMap
        return Object.keys(map).some(applicationId => map[applicationId].type === applicationType)
      }

      const isQaAutomation = queryUtil.isParameterTrue.bind(queryUtil, 'isqa')
      const isTpaIntegration = queryUtil.isParameterTrue.bind(queryUtil, 'isTpaIntegration')
      const isWixCodeIntegration = queryUtil.isParameterTrue.bind(queryUtil, 'isWixCodeIntegration')

      function insert(p, marker) {
        const extra = Array.prototype.slice.call(arguments, 2)
        const pos = p.indexOf(marker)
        if (pos !== -1) {
          pkgs.splice.apply(p, [pos, 0].concat(extra))
        } else {
          pkgs.push.apply(p, extra)
        }
      }

      function isWixDomain() {
        return window.location.hostname === 'www.wix.com'
      }

      function isWixSites() {
        return isWixDomain() || queryUtil.isParameterTrue('iswixsite')
      }

      function isWixCloud() {
        // We check that we have wixCodeModel, because if a site is published without Wix Code,
        // And then Wix Code is added and the site is saved, the metasite is "copied" to the
        // published site as well, showing as if there's Wix Code in the published site, although
        // there isn't
        return shouldLoadPackageFor('siteextension') && !!siteModel.rendererModel.wixCodeModel
      }

      function shouldLoadPackageForEComMigratedSite() {
        return areMetaSiteFlagsExist() && siteModel.publicModel.metaSiteFlags.indexOf('EComMigrated') >= 0
      }

      function areMetaSiteFlagsExist() {
        return siteModel.publicModel && siteModel.publicModel.metaSiteFlags
      }

      if (isPreview || shouldLoadPackageFor('wixapps') || shouldLoadPackageFor('ecommerce') || shouldLoadPackageForEComMigratedSite()) {
        insert(pkgs, 'tpa', 'wixappsCore', 'wixappsClassics')
      }
      if (isPreview || shouldLoadPackageFor('appbuilder')) {
        insert(pkgs, 'tpa', 'wixappsCore', 'wixappsBuilder')
      }
      if (queryUtil.isParameterTrue('ds') || isPreview || isExternalPreview) {
        pkgs.push('componentsPreviewLayer')
      }
      if (queryUtil.isParameterTrue('ds') || isPreview) {
        pkgs.push('documentServices', 'documentServicesSchemas')
      }
      if (isPreview) {
        pkgs.push('immutable')
      }
      if (isQaAutomation()) {
        pkgs.push('qaAutomation', 'react')
      }
      if (isWixSites()) {
        pkgs.push('wixSites')
      }
      if (isTpaIntegration()) {
        pkgs.push('tpaIntegration', 'jasmine', 'jasmine-html')
      }
      if (isWixCodeIntegration()) {
        pkgs.push('wixCodeIntegration', 'jasmine', 'jasmine-html')
      }
      if (isWixCloud() || isPreview) {
        pkgs.push('wixCode')
      }

      return pkgs
    }

    const handleRequireError = prefetch ?
      () => {} :
      pkgs =>
        err => {
          if (!config.urlArgs) {
            if (err.requireModules) {
              err.requireModules.forEach(module => {
                requirejs.undef(module)
              })
            }
            config.urlArgs = `c=${Date.now()}`
            requirejs.config(config)
            const errClone = Object.keys(err).reduce((r, k) => {
              r[k] = err[k]
              return r
            }, {})
            errClone.message = err.message
            errClone.stack = err.stack ? '' + err.stack : '' //eslint-disable-line prefer-template
            requirejs(pkgs, () => {
              errClone.errn = 'REQUIREJS_RETRY_ERROR'
              errClone.errc = 111025
              errClone.severity = 10
              requirejs.onError(errClone)
            }, requirejs.onError)
          }
        }

    function loadAdditional(pkgs) {
      requirejs(pkgs,
        function () {
          const p = buildFunctionParametersObject(pkgs, arguments)

          function buildFunctionParametersObject(_pkgs, args) {
            return _pkgs.reduce((result, pkg, index) => {
              result[pkg] = args[index]
              return result
            }, {})
          }

          function initConditionalDependencies(_pkgs) {
            if (_pkgs.qaAutomation) {
              _pkgs.qaAutomation.init(window, siteModel)
            }
            if (_pkgs.tpaIntegration) {
              _pkgs.tpaIntegration.init(window, p.core)
            }
            if (_pkgs.wixCodeIntegration) {
              _pkgs.wixCodeIntegration.init(window, p.core, p.wixCode, p.widgets, p.utils)
            }
          }

          initConditionalDependencies(p)
        },
        handleRequireError(pkgs))
    }

    function load(pkgs, callback) {
      /*
       * Require all needed packages (static + conditional)
       * Then do initial site render (or re-layout if it was rendered by the server)
       */
      function getAjaxHandler($) {
        const ignore = {}

        const isXhrWithCredentials = (function () {
          try {
            const xhr = new window.XMLHttpRequest()
            return 'withCredentials' in xhr
          } catch (e) {
            return false
          }
        }())

        function setCallbacks(xhr, options) {
          xhr.onerror = function (e) { //eslint-disable-line no-unused-vars
            if (options.error) {
              options.error(xhr)
            }
          }
          xhr.onload = function () {
            if (this.status >= 200 && this.status < 300) {
              if (options.success) {
                let response = null
                try {
                  response = JSON.parse(xhr.responseText)
                } catch (e) {
                  response = xhr.responseText
                }
                options.success(response)
              }
            } else {
              this.onerror()
            }
          }
        }

        function canUseCache(options) {
          return !ignore[options.url] &&
                        (!options.type || options.type.toUpperCase() === 'GET')
        }

        function error(msg) {
          try {
            console.error(msg)
          } catch (e) {
            // empty
          }
        }

        return function ajax(options) {
          if (options.cache !== false && canUseCache(options) && contentCache(options)) {
            return
          }
          ignore[options.url] = true

          if (isXhrWithCredentials) {
            $.ajax.apply($, arguments)
          } else if (typeof XDomainRequest !== 'undefined') {
            /*globals XDomainRequest:true*/
            const xhr = new XDomainRequest()
            const httpMethod = options.type || 'GET'
            xhr.open(httpMethod, options.url)
            setCallbacks(xhr, options)
            xhr.setRequestHeader = function () {
            } // ignores request headers in IE (not supported)
            xhr.send()
          } else {
            error('XHR cors not supported, and neither is XDR')
          }
        }
      }

      requirejs(pkgs, function () {
        wixBiSession.packagesLoaded = Date.now()

        function buildFunctionParametersObject(_pkgs, args) {
          return _pkgs.reduce((result, pkg, index) => {
            result[pkg] = args[index]
            return result
          }, {})
        }

        const p = buildFunctionParametersObject(pkgs, arguments)
        const ajaxHandler = getAjaxHandler(p.zepto)
        p.coreUtils.ajaxLibrary.register(ajaxHandler)

        if (siteModel.wixData) {
          const siteStructureNode = window.document.getElementById('masterPage') || window.document.getElementById('SITE_STRUCTURE')
          siteModel.wixHtmlRaw = siteStructureNode.outerHTML
          siteModel.wixAnchors = window.anchors || {}
          siteStructureNode.parentNode.removeChild(siteStructureNode)
        }

        siteModel.requestModel = {
          userAgent: window.navigator.userAgent,
          cookie: window.document.cookie,
          storage: p.coreUtils.storage(window)
        }
        siteModel.currentUrl = p.coreUtils.urlUtils.parseUrl(window.serverSideUrl || window.location.href)
        siteModel.forceMobileView = window.forceMobileView

        callback(p, ajaxHandler)
      }, handleRequireError(pkgs))
    }

    function getClientSidePackages() {
      const clientSidePackages = ['lodash', 'coreUtils', 'skins', 'components', 'core', 'mobx', 'mobx-react', 'color',
        'zepto', 'TimelineMax', 'animations', 'react', 'layout', 'tpa', 'dataFixer', 'siteUtils',
        'translationsUtils', 'hammer', 'utils', 'fonts', 'xss', 'platformUtils', 'loggingUtils',
        'imageClientLib', 'swfobject', 'santaProps', 'displayer', 'wixUrlParser', 'mousetrap',
        'DrawSVGPlugin', 'reactDOM', 'ScrollToPlugin', 'widgets', 'experiment', 'render', 'reactDOMServer',
        'imageCommon', 'formCommon', 'textCommon', 'containerCommon', 'socialCommon', 'galleriesCommon', 'buttonCommon',
        'compDesignUtils', 'mediaCommon', 'audioCommon', 'bluebird', 'skinExports', 'compUtils',
        // Components
        'fileUploader', 'wixSkinOnly', 'pinItPinWidget', 'siteButton', 'popupCloseTextButton', 'image', 'zoomedImage',
        'wPhoto', 'clipArt', 'displayer', 'matrixGallery', 'wRichText', 'paginatedGridGallery', 'container',
        'radioButton', 'radioGroup', 'headerContainer', 'footerContainer', 'screenWidthContainer', 'documentMedia',
        'adminLoginButton', 'backgroundCommon', 'balata', 'datePicker', 'contactForm', 'subscribeForm',
        'textArea', 'video', 'loginButton', 'googleMap', 'mediaContainerFactory', 'soundCloudWidget', 'paypalButton',
        'imageButton', 'linkBar', 'comboBoxInput', 'spotifyPlayer', 'spotifyFollow', 'twitterFeed', 'backToTopButton',
        'svgShape', 'facebookLike', 'facebookComments', 'facebookLikeBox', 'rssButton', 'textInput', 'tinyMenu',
        'groupContainer', 'pinterestPinIt', 'pinterestFollow', 'wTwitterFollow', 'audioPlayer', 'column',
        'htmlComponent', 'deadComponent',

        'pmrpc', 'tweenEngine', 'multilingual'
      ]
      addConditionalDependencies(clientSidePackages)
      return clientSidePackages
    }

    function renderClientSide(privates) {
      const clientSidePackages = getClientSidePackages()
      const wixCodeInitPkg = ['wixCodeInit']
      requirejs(wixCodeInitPkg, wixCodeInit => {
        const wixCodeAppApi = wixCodeInit.getAppApi()

        load(clientSidePackages, (loadedPackages, ajaxHandler) => {
          window.Promise = window.Promise || loadedPackages.bluebird

          if (privates) {
            loadedPackages.render.clientSideWithPrivates(loadedPackages, privates, {
              wixCodeAppApi
            }, notifyParent.bind(null, 'santaReady'))
          } else {
            loadedPackages.render.clientSide(loadedPackages, ajaxHandler, siteModel, {
              wixCodeAppApi
            }, notifyParent.bind(null, 'santaReady'))
          }
        })

        wixCodeInit.initMainR(wixCodeAppApi, siteModel, mobileView, queryUtil)
      }, handleRequireError(wixCodeInitPkg))

      loadAdditional(clientSidePackages)

      miniPackages(window, requirejs, isServerSide)
    }

    function renderServerSide() {
      load(['lodash', 'zepto', 'coreUtils', 'coreUtilsLib', 'loggingUtils', 'siteUtils', 'experiment', 'imageClientApi',
        'imageClientLib', 'skinExports', 'wixUrlParser', 'layout', 'warmup', ...window.Promise ? [] : ['bluebird']], loadedPackages => {
        window.Promise = window.Promise || loadedPackages.bluebird

        loadedPackages.warmup.init(siteModel, privates => {
          notifyParent('santaDidLayout')
          renderClientSide(privates)
        })
      })
    }

    // Server side render state
    window.sssr = {}

    function callRender() {
      if (defineExperiment) {
        defineExperiment(window)
      }
      if (isServerSide) {
        renderServerSide()
      } else {
        renderClientSide()
      }
    }


    if (isLocal(window.santaBase) && queryUtil.getParameterByName('hot') !== 'false') {
      requirejs(['hot', 'zepto'], (hot, $) => {
        hot.init($)
        callRender()
      })
    } else {
      callRender()
    }
  }

  return render
}



// WEBPACK FOOTER //
// ./~/santa-main-r/src/main/lib/viewer/render.js